import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';

/**
 * Champ de mot de passe accessible avec bouton « révéler » (œil).
 *
 * Bascule entre `type="password"` et `type="text"` pour permettre de voir le mot
 * de passe en clair. Le bouton de bascule est TOUJOURS `type="button"` (il ne
 * soumet jamais le formulaire englobant), reflète l'état via `aria-pressed` et
 * porte un `aria-label` i18n (afficher / masquer). Réutilisable partout où un
 * `type="password"` existe (login, réglages, admin). Tokens sémantiques.
 */
export interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string | undefined;
  autoComplete?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  autoFocus?: boolean | undefined;
  minLength?: number | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
  /** Classes appliquées à l'`<input>` (mêmes tokens que les autres champs). */
  className?: string | undefined;
}

const DEFAULT_INPUT_CLASS =
  'rounded-md border border-border-strong bg-surface px-3 py-2 text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

export function PasswordInput({
  id,
  value,
  onChange,
  name,
  autoComplete,
  placeholder,
  required,
  autoFocus,
  minLength,
  className,
  ...aria
}: PasswordInputProps): React.JSX.Element {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        minLength={minLength}
        value={value}
        onChange={onChange}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        className={`${className ?? DEFAULT_INPUT_CLASS} w-full pr-11`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-pressed={visible}
        aria-label={visible ? t('passwordField.hide') : t('passwordField.show')}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {visible ? (
          <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
        ) : (
          <EyeIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
